import type { CSSProperties, JSX } from 'react'
import type { BlastRadius as Radius, BuildingTypeConfigReach, ConfigDiff } from '../api.js'
import { asWords, formatKw, formatMoney, grouped } from './Pill.js'
import { T } from '../tokens.js'

/**
 * The blast-radius preview (R-13, spec §7.8) — *"what would change if I saved
 * this"*, drawn as the wireframe's dark side panel (artboard **1f**, "Blast
 * radius — before you save").
 *
 * ## Two figures the wireframe does not draw, and both are mandatory
 *
 * 1f headlines a bare *"1,104 accounts move"* and goes straight to Direction.
 * A panel that says only that is decoration, because it cannot distinguish the
 * two answers a program manager most needs told apart:
 *
 * - **`sampled`.** The computation is 62 ms per account against the seeded
 *   book's 313 in-scope accounts, so an exhaustive preview is ≈40 s and the
 *   console asks for a bounded one. A bounded answer presented as a census is
 *   the panel actively lying: *"18 accounts move"* out of 25 scored is a rate
 *   over a twelfth of the book, and a reader who takes it for a book-wide count
 *   under-reads the change by an order of magnitude. So the sample is stated in
 *   the dark block, immediately under the headline, in the same visual weight
 *   as the caveat it is — not in a footnote below the fold.
 * - **`blindTo`.** `computeBlastRadius` evaluates `scoreAccount` and nothing
 *   else, so fifteen configuration paths — eligibility, cohort, ranking
 *   weights, the day-of adjustment, the strategy weights, the negligible-share
 *   threshold, the two note fields, the timezone, three event-length and lead
 *   fields, the settlement interval, the prior-event-day exclusion and the
 *   shortfall tolerance — move nothing here and, several of them, a great deal
 *   on commit. *"Nothing moves"* and *"I do not look at that path"* read
 *   identically as a zero, and the second one is how a screen that looks like
 *   verification launders an edit nobody checked. Non-empty `blindTo` is
 *   therefore a warning block above the numbers, not a caveat under them.
 *
 *   The list is per **path** rather than per section, and it was per section
 *   until four unread leaves inside evaluated sections — `timezone`,
 *   `events.notificationLeadMinutes`, `events.maxLengthHours`,
 *   `baseline.settlementIntervalMinutes` — were each measured on the wire
 *   coming back `blindTo: []` under a green *Fully evaluated* banner. Hence
 *   "path" in the copy below.
 *
 * Both are stated in **both** directions. "Every in-scope account was scored"
 * and "every section this draft changes is one this preview evaluates" are
 * facts worth reading; a warning that is only ever present when things are bad
 * teaches a reader to skim past the block entirely.
 *
 * ## `data-coverage`
 *
 * `complete` when `blindTo` is empty and the preview was exhaustive, `partial`
 * otherwise. It is the machine-readable form of the paragraph above: a test
 * that wants to know whether a headline of zero means anything reads this
 * rather than the prose.
 *
 * ## Confidence changes are their own region, deliberately
 *
 * A band change is more consequential than a kW change and much easier to miss
 * — the number on screen does not move — so `confidence-changes` is a sibling
 * of `kw-movement`, never a row inside it. The two regions do not nest and
 * neither repeats the other's figures.
 *
 * ## It renders; it does not compute
 *
 * Every number here is a field of the server's `BlastRadius`. Nothing is summed,
 * differenced or re-derived in the browser (R-17) — the histogram's bar heights
 * are the one exception, and they are a scale, not a figure.
 */

const panel: CSSProperties = {
  border: `1.5px solid ${T.color.ink}`,
  background: '#fff',
}

const section: CSSProperties = {
  padding: '12px 16px',
  borderTop: `1px solid ${T.color.hairline}`,
}

const microLabel: CSSProperties = {
  fontSize: T.micro.size,
  fontWeight: T.weight.semi,
  letterSpacing: T.micro.tracking,
  textTransform: T.micro.transform,
  color: T.color.micro,
}

const figure: CSSProperties = {
  fontSize: 18,
  fontWeight: T.weight.bold,
  color: T.color.ink,
  fontVariantNumeric: 'tabular-nums',
}

const note: CSSProperties = {
  fontSize: 12.5,
  lineHeight: 1.45,
  color: T.color.body,
  margin: '6px 0 0',
}

/** The seven fixed buckets, as a proportion of the tallest. A scale, not a figure. */
function barHeights(distribution: Radius['distribution']): number[] {
  const tallest = distribution.reduce((most, bucket) => Math.max(most, bucket.count), 0)
  return distribution.map((bucket) => (tallest === 0 ? 0 : (bucket.count / tallest) * 100))
}

/** `-13.2%`, with the minus sign a reader can tell from a hyphen. */
function percent(value: number): string {
  return `${value < 0 ? '−' : ''}${grouped(Math.abs(value), 1)}%`
}

function DiffValue({ value }: { value: unknown }): JSX.Element {
  if (value === null) return <>none</>
  if (typeof value === 'object') return <>{JSON.stringify(value)}</>
  return <>{String(value)}</>
}

/**
 * The second kind of zero, in one sentence per building type the draft edits.
 *
 * `blindTo` answers *"the preview did not look at what you changed"*. It
 * structurally cannot answer *"it looked, and nothing on this book can feel
 * it"*, because that is a fact about the accounts rather than about the path —
 * and `buildingTypes` is on the evaluated list precisely because a
 * building-type edit **can** move an estimate.
 *
 * The measured case this exists for: editing `k12_school`'s summer occupancy
 * factor moves **0 of 313** accounts exhaustively, and reports `blindTo: []`
 * under the green *"Fully evaluated · every path this draft changes is one this
 * preview evaluates"*. Both true, and together they read as "this edit does
 * nothing". What is true is that 15 in-scope accounts resolve to `k12_school`,
 * all 15 score by the fallback method, and `fallbackProfile` applies no
 * occupancy calendar — so the zero belongs to this book rather than to the
 * edit, and a book with one decomposable school in it would answer differently.
 *
 * The occupancy clause is drawn only when the edit is occupancy-only, because
 * on any other building-type path the fallback method is not the reason.
 */
function reachSentence(row: BuildingTypeConfigReach): string {
  const label = asWords(row.buildingType)
  if (row.accountsResolving === 0) {
    return `${label} — no scored account resolves to this building type, so nothing here can move `
      + 'on it. That is a fact about the book, not about the edit.'
  }
  const all = row.scoredByFallback === row.accountsResolving
  const scored = `${label} — ${grouped(row.accountsResolving, 0)} scored `
    + `${row.accountsResolving === 1 ? 'account resolves' : 'accounts resolve'} to it, `
  if (!row.occupancyOnly) {
    return `${scored}${grouped(row.scoredByFallback, 0)} of them by the fallback method.`
  }
  return all
    ? `${scored}and every one of them scores by the fallback method, which applies no occupancy `
      + 'calendar. An occupancy edit cannot move them, however the factor is set.'
    : `${scored}and ${grouped(row.scoredByFallback, 0)} of them score by the fallback method, `
      + 'which applies no occupancy calendar. Only the rest can feel an occupancy edit.'
}

export interface BlastRadiusProps {
  radius: Radius
  /** What the draft changes, from the same response. Rendered, never re-derived here. */
  diff: ConfigDiff[]
}

export function BlastRadius({ radius, diff }: BlastRadiusProps): JSX.Element {
  const { sampled, blindTo, reach, shortfallExposure: exposure } = radius
  const complete = blindTo.length === 0 && sampled === null
  const heights = barHeights(radius.distribution)

  return (
    <div
      data-testid="blast-radius"
      data-coverage={complete ? 'complete' : 'partial'}
      data-sampled={sampled === null ? 'false' : 'true'}
      data-blind-to={blindTo.join(' ')}
      style={panel}
    >
      <div style={{ padding: '14px 16px', background: T.color.ink, color: '#fff' }}>
        <div style={{ ...microLabel, color: T.color.amber }}>Blast radius — before you save</div>
        <div style={{ fontSize: 20, fontWeight: T.weight.bold, marginTop: 5 }}>
          {`${grouped(radius.accountsMoved, 0)} accounts move`}
        </div>
        {/*
          The denominator, on the line under the headline rather than beside it:
          `accountsMoved` is a count over exactly the accounts that were scored,
          and a reader who cannot see how many that was cannot read the figure
          above at all.
        */}
        <div
          data-testid="blast-radius-sample"
          data-exhaustive={sampled === null ? 'true' : 'false'}
          style={{ fontSize: 12.5, lineHeight: 1.45, color: '#fff', marginTop: 6, opacity: 0.92 }}
        >
          {sampled === null
            ? `Exact — every one of the ${grouped(radius.accountsConsidered, 0)} in-scope accounts was scored.`
            : `Sampled — ${grouped(sampled.sampled, 0)} of ${grouped(sampled.of, 0)} in-scope accounts were scored. `
              + 'Every figure here counts that sample, not the book.'}
        </div>
      </div>

      {/*
        Above the numbers, not below them. A reader who has already taken
        "0 accounts move" as an answer will not scroll to find out that the
        preview never looked at the section they edited.
      */}
      <div
        data-testid="blast-radius-blind"
        data-blind={blindTo.length === 0 ? 'false' : 'true'}
        style={{
          ...section,
          borderTop: 0,
          background: blindTo.length === 0 ? T.tint.green : T.tint.stale,
        }}
      >
        <div style={{ ...microLabel, color: blindTo.length === 0 ? T.color.money : T.color.warnInk }}>
          {blindTo.length === 0 ? 'Fully evaluated' : 'Not evaluated by this preview'}
        </div>
        {blindTo.length === 0
          ? (
            <p style={{ ...note, color: T.color.body }}>
              Every path this draft changes is one this preview evaluates.
            </p>
          )
          : (
            <>
              <ul style={{ margin: '7px 0 0', paddingLeft: 18, fontSize: 12.5, color: T.color.warnInk }}>
                {blindTo.map((path) => <li key={path}><code>{path}</code></li>)}
              </ul>
              <p style={{ ...note, color: T.color.warnInk }}>
                The figures above do not include these. A zero here is not evidence that they change nothing.
              </p>
            </>
          )}
      </div>

      {/*
        Above the numbers for `blindTo`'s reason, and separate from it for the
        opposite one: these paths WERE evaluated. See `reachSentence`.
      */}
      {reach.length === 0 ? null : (
        <div
          data-testid="blast-radius-reach"
          data-reach={reach.map((row) => row.buildingType).join(' ')}
          style={{ ...section, borderTop: 0, background: T.tint.stale }}
        >
          <div style={{ ...microLabel, color: T.color.warnInk }}>What this book has of it</div>
          <ul style={{ margin: '7px 0 0', paddingLeft: 18, fontSize: 12.5, color: T.color.warnInk, lineHeight: 1.5 }}>
            {reach.map((row) => (
              <li key={row.buildingType} data-testid={`reach-${row.buildingType}`}>
                {reachSentence(row)}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div data-testid="kw-movement" style={section}>
        <div style={microLabel}>Direction</div>
        <div style={{ display: 'flex', gap: 18, marginTop: 8 }}>
          {([
            ['down', radius.direction.down],
            ['up', radius.direction.up],
            ['unchanged', radius.direction.unchanged],
          ] as const).map(([label, count]) => (
            <div key={label} data-testid={`direction-${label}`}>
              <div style={figure}>{grouped(count, 0)}</div>
              <div style={{ fontSize: 12, color: T.color.micro }}>{label}</div>
            </div>
          ))}
        </div>

        <div style={{ ...microLabel, marginTop: 16 }}>Distribution of change</div>
        {/*
          Seven bars, always — `distribution` carries every bucket including the
          empty ones, so the histogram has a fixed geometry rather than one that
          reshapes with the data and invites a reader to compare two previews
          drawn on different axes.
        */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 64, marginTop: 10 }}>
          {radius.distribution.map((bucket, index) => (
            <div
              key={bucket.bucket}
              title={`${bucket.bucket}: ${grouped(bucket.count, 0)}`}
              data-bucket={bucket.bucket}
              data-count={bucket.count}
              style={{
                width: 20,
                height: `${heights[index] ?? 0}%`,
                minHeight: bucket.count === 0 ? 0 : 2,
                background: index === radius.distribution.length - 1 ? T.color.red : T.color.outOfWindow,
              }}
            />
          ))}
        </div>
        {radius.kwPercentChange === null
          ? <p style={note}>No account’s nomination moved, so there is no percentage to plot.</p>
          : (
            <div
              data-testid="kw-percent-change"
              style={{
                display: 'flex', justifyContent: 'space-between',
                fontSize: 12, color: T.color.micro, marginTop: 5,
              }}
            >
              <span>{percent(radius.kwPercentChange.min)}</span>
              <span>{`${percent(radius.kwPercentChange.median)} median`}</span>
              <span>{percent(radius.kwPercentChange.max)}</span>
            </div>
          )}
      </div>

      {/*
        A sibling of `kw-movement`, never a row inside it. The number on screen
        does not move when a band does, which is what makes this the change a
        reader misses.
      */}
      <div data-testid="confidence-changes" data-count={radius.confidenceChanges.length} style={section}>
        <div style={microLabel}>Confidence band changes</div>
        {radius.confidenceChanges.length === 0
          ? <p style={note}>No account changes confidence band.</p>
          : (
            <>
              <p style={{ ...note, color: T.color.warnInk }}>
                {`${grouped(radius.confidenceChanges.length, 0)} `}
                {radius.confidenceChanges.length === 1 ? 'account changes' : 'accounts change'}
                {' confidence band. The kW figure a rep reads does not move when this does.'}
              </p>
              <ul style={{ margin: '7px 0 0', paddingLeft: 18, fontSize: 12.5, color: T.color.body }}>
                {radius.confidenceChanges.slice(0, 8).map((change) => (
                  <li key={change.accountId} data-account-id={change.accountId}>
                    {`${change.accountId} · ${change.from} → ${change.to}`}
                  </li>
                ))}
              </ul>
              {radius.confidenceChanges.length > 8
                ? (
                  <p style={{ ...note, color: T.color.micro }}>
                    {`and ${grouped(radius.confidenceChanges.length - 8, 0)} more.`}
                  </p>
                )
                : null}
            </>
          )}
      </div>

      <div data-testid="moved-by" style={section}>
        <div style={microLabel}>What moved</div>
        <div style={{ marginTop: 7, fontSize: 12.5, color: T.color.body, lineHeight: 1.6 }}>
          {/*
            An account that moved on two dimensions is counted in both, so these
            do not sum to the headline — said here rather than left to a reader
            who adds them up and finds the arithmetic broken.
          */}
          <div>{`Nomination kW — ${grouped(radius.movedBy.nominationKw, 0)}`}</div>
          <div>{`Season value — ${grouped(radius.movedBy.seasonValue, 0)}`}</div>
          <div>{`Confidence band — ${grouped(radius.movedBy.confidence, 0)}`}</div>
          <div>{`Flags — ${grouped(radius.movedBy.flags, 0)}`}</div>
        </div>
        <p style={{ ...note, color: T.color.micro }}>
          An account that moved two ways is counted in both, so these do not sum to the headline.
        </p>
      </div>

      <div data-testid="largest-change" style={section}>
        <div style={microLabel}>Largest single-account change</div>
        {radius.largestChange === null
          ? <p style={note}>Nothing moved, so there is no largest change.</p>
          : (
            <div style={{ marginTop: 6 }}>
              <div style={{ fontSize: 13.5, fontWeight: T.weight.semi }}>{radius.largestChange.name}</div>
              <div style={{ fontSize: 12.5, color: T.color.body, marginTop: 3 }}>
                {`${formatKw(radius.largestChange.fromKw)} → ${formatKw(radius.largestChange.toKw)}`}
              </div>
              <div style={{ fontSize: 12.5, color: T.color.body }}>
                {`${formatMoney(radius.largestChange.fromSeasonValue)} → `
                  + `${formatMoney(radius.largestChange.toSeasonValue)} season value`}
              </div>
            </div>
          )}
      </div>

      {/*
        Three sentences, because one number could not carry three facts without
        misattributing two of them. `newlyExposed` is the only one the commit
        causes, so it is the only one in warning ink and the only one allowed to
        say "committing"; `standing` is a property of the book and says so in
        muted ink under it. See `BlastRadius.shortfallExposure` in `api.ts`.
      */}
      <div
        data-testid="also-affected"
        data-newly-exposed={exposure.newlyExposed}
        data-relieved={exposure.relieved}
        data-standing={exposure.standing}
        style={section}
      >
        <div style={microLabel}>Also affected</div>
        <p
          data-testid="newly-exposed"
          style={{ ...note, color: exposure.newlyExposed === 0 ? T.color.body : T.color.warnInk }}
        >
          {exposure.newlyExposed === 0
            ? 'No enrolled account is newly put into contractual shortfall exposure by this commit.'
            : `${grouped(exposure.newlyExposed, 0)} enrolled `
              + (exposure.newlyExposed === 1
                ? 'account would be newly put into contractual shortfall exposure by this commit — '
                  + 'its nomination of record is above the estimate this draft produces for it, '
                  + 'and is not above the estimate the configuration in force produces.'
                : 'accounts would be newly put into contractual shortfall exposure by this commit — '
                  + 'their nominations of record are above the estimates this draft produces for them, '
                  + 'and are not above the estimates the configuration in force produces.')}
        </p>
        {exposure.relieved === 0
          ? null
          : (
            <p data-testid="exposure-relieved" style={{ ...note, color: T.color.money }}>
              {`${grouped(exposure.relieved, 0)} enrolled `
                + (exposure.relieved === 1
                  ? 'account would come out of that exposure on this commit.'
                  : 'accounts would come out of that exposure on this commit.')}
            </p>
          )}
        <p data-testid="exposure-standing" style={{ ...note, color: T.color.micro }}>
          {exposure.standing === 0
            ? 'No enrolled account is nominated above the estimate the configuration in force '
              + 'produces for it today.'
            : `${grouped(exposure.standing, 0)} enrolled `
              + (exposure.standing === 1
                ? 'account is already nominated above the estimate the configuration in force '
                  + 'produces for it. That is true today, with or without this commit.'
                : 'accounts are already nominated above the estimates the configuration in force '
                  + 'produces for them. That is true today, with or without this commit.')}
        </p>
      </div>

      <div data-testid="config-diff" data-count={diff.length} style={section}>
        <div style={microLabel}>{`What this draft changes (${grouped(diff.length, 0)})`}</div>
        {diff.length === 0
          ? <p style={note}>Nothing. This draft is the configuration in force.</p>
          : (
            <ul style={{ margin: '7px 0 0', paddingLeft: 18, fontSize: 12.5, color: T.color.body }}>
              {diff.slice(0, 12).map((change) => (
                <li key={change.path} data-path={change.path}>
                  <code>{change.path}</code>
                  {': '}
                  <DiffValue value={change.from} />
                  {' → '}
                  <b><DiffValue value={change.to} /></b>
                </li>
              ))}
            </ul>
          )}
        {diff.length > 12
          ? <p style={{ ...note, color: T.color.micro }}>{`and ${grouped(diff.length - 12, 0)} more.`}</p>
          : null}
      </div>
    </div>
  )
}
